import React, { useState, useEffect } from 'react';
import { Search, X, Info } from 'lucide-react';

const SearchBar = ({ userRole, searchTerm, onSearchChange }) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [showHelp, setShowHelp] = useState(false);

  // Debounce search to avoid too many updates
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(localSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearchTerm, onSearchChange]);

  // Update local term when prop changes
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Get search fields based on role (Dashboard.md requirements)
  const getSearchFields = (role) => {
    switch (role) {
      case 'Administrador':
      case 'Transportista':
        return ['Manifiesto', 'Referencia', 'Conocimiento de Embarque'];
      case 'Cliente':
      case 'Notificado':
        return ['Conocimiento de Embarque'];
      default:
        return [];
    }
  };

  const searchFields = getSearchFields(userRole);
  
  const getPlaceholder = () => {
    if (searchFields.length === 0) return 'Búsqueda no disponible';
    
    if (searchFields.length === 1) {
      return `Buscar por ${searchFields[0]}...`;
    }
    
    return `Buscar por ${searchFields.slice(0, -1).join(', ')} o ${searchFields.slice(-1)}...`;
  };

  const clearSearch = () => {
    setLocalSearchTerm('');
    onSearchChange('');
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        
        <input
          type="text"
          className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition duration-150 ease-in-out"
          placeholder={getPlaceholder()}
          value={localSearchTerm}
          onChange={(e) => setLocalSearchTerm(e.target.value)}
          disabled={searchFields.length === 0}
        />
        
        <div className="absolute inset-y-0 right-0 flex items-center">
          {localSearchTerm && (
            <button
              onClick={clearSearch}
              className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition duration-150 ease-in-out"
              title="Limpiar búsqueda"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="p-2 mr-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition duration-150 ease-in-out"
            title="Ayuda de búsqueda"
          >
            <Info className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Search Help */}
      {showHelp && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                Campos de Búsqueda para {userRole}
              </h4>
              
              {searchFields.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm text-blue-800">
                    Puedes buscar por los siguientes campos:
                  </p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    {searchFields.map((field, index) => (
                      <li key={index} className="flex items-center">
                        <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                        {field}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <p className="text-xs text-blue-600">
                      💡 <strong>Consejo:</strong> La búsqueda se actualiza automáticamente mientras escribes
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-blue-800">
                  La búsqueda no está disponible para tu rol actual.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search Results Summary */}
      {localSearchTerm && (
        <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
          <div className="flex items-center text-sm text-gray-600">
            <Search className="h-4 w-4 mr-2" />
            <span>Buscando: <strong>"{localSearchTerm}"</strong></span>
          </div>
          
          <button
            onClick={clearSearch}
            className="text-sm text-gray-500 hover:text-gray-700 transition duration-150 ease-in-out"
          >
            Limpiar
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchBar;